// =============================================================================
// app/src/paginas/ContasPagarFormularios.tsx — os dois formulários de conta.
//
// Extraídos da tela (23/08/2026): ContasPagar.tsx tinha 725 linhas e ia
// ganhar seleção múltipla, lote e agrupamento — os formulários são a parte
// que NÃO muda com nada disso, então saem da frente da revisão.
//
// A conta avulsa e a REGRA (conta fixa que o gerador materializa 12 meses à
// frente). O vocabulário de categorias mora aqui e é exportado: a tela usa
// para dar rótulo e cor às linhas.
// =============================================================================
import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { CategoriaConta, ContaPagar, ContaRecorrente } from '@core/types';
import type { ReactNode } from 'react';
import type { CampoSugerido } from '../lib/sugestao';
import Aviso from '../componentes/Aviso';
import {
  fornecedores, salvarComprovanteConta, salvarContaECodigo, salvarContaRecorrente,
} from '../lib/consultas';
import { conferirCodigo, dadosDoCodigo, formatarLinha, mesmoTitulo, soDigitos } from '../lib/codigoBarras';
import { prepararComprovante } from '../lib/comprovante';
import { contasParecidas, valorDaConta } from '../lib/contasPagar';
import { brl, hojeISO, paraNumero } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';

export const CATEGORIAS: Array<[CategoriaConta, string]> = [
  ['aluguel', 'Aluguel'], ['energia', 'Energia'], ['agua', 'Água'],
  ['internet', 'Internet'], ['telefone', 'Telefone'], ['salarios', 'Salários'],
  ['pro_labore', 'Pró-labore'], ['contador', 'Contador'], ['impostos', 'Impostos'],
  ['taxa_cartao', 'Taxa de cartão'], ['seguro', 'Seguro'], ['alvara', 'Alvará e taxas'],
  ['fornecedores', 'Fornecedores (mercadoria)'], ['manutencao', 'Manutenção'],
  ['outros', 'Outros'],
  // 081: por último de propósito — é a saída que NÃO é da loja, e ficar no
  // fim da lista evita que alguém a escolha por engano no lugar de 'outros'.
  ['retirada', 'Retirada (não é da loja)'],
];
export const rotulo = (c: string) => CATEGORIAS.find(([k]) => k === c)?.[1] ?? c;

/** A trava contra o salário em dobro (094): o salário de cada pessoa já
 *  aparece em Contas a pagar pela folha, e o fluxo de caixa soma a folha em
 *  aberto. Uma conta "Salário — Fulano" seria contada duas vezes. A categoria
 *  continua existindo para o que NÃO está na folha: FGTS e INSS. */
export const DICA_DA_CATEGORIA_SALARIOS =
  'O salário de cada pessoa já aparece aqui pela folha — não cadastre de novo. '
  + 'Use esta categoria para FGTS e INSS.';

/** O que o cadastro pelo boleto (ContasPagarBoleto) já sabe da conta nova. */
export interface PreenchidoPeloBoleto {
  descricao: string;
  categoria: CategoriaConta;
  fornecedorId: string | null;
  valor: number | null;
  /** Nulo na conta de convênio: o código não traz a data. */
  vencimento: string | null;
  codigo: string;
  /** O arquivo lido, para guardar como anexo da conta depois de salvar. */
  arquivo: File | null;
  /** Quais destes campos são PALPITE, e não leitura do código de barras. O
   *  valor e o vencimento vêm do código e não entram aqui: são fato. */
  palpites?: CampoSugerido[];
  /** De onde veio o palpite, em uma frase — a tela mostra junto da marca. */
  porque?: string | null;
}

/**
 * A marca de PALPITE num campo, no molde do selo da ficha da placa: borda fina,
 * `text-xs font-bold`, cor `alerta`. O rótulo fica à esquerda e o selo à
 * direita, na mesma linha — é a forma que a casa já usa.
 *
 * O que ela NÃO faz: impedir. O campo continua editável e o palpite continua
 * preenchido. O que muda é o dono saber, antes de salvar, que aquele valor não
 * veio do código de barras nem da mão dele — veio de o sistema achar.
 */
function MarcaDePalpite() {
  return (
    <span className="selo selo-hoje">
      palpite
    </span>
  );
}

/** Rótulo com a marca à direita, quando o campo for palpite. */
function RotuloDoCampo(
  { para, children, palpite }: { para: string; children: ReactNode; palpite: boolean },
) {
  return (
    <div className="flex items-center justify-between gap-2">
      <label className="rotulo" htmlFor={para}>{children}</label>
      {palpite && <MarcaDePalpite />}
    </div>
  );
}

const reais = (v: number | null) => (v === null ? '' : v.toFixed(2).replace('.', ','));
const dataBR = (iso: string) => iso.split('-').reverse().join('/');

/** Uma conta avulsa (a "flexível"), ou o ajuste de uma gerada pela regra. */
export function FormularioConta({ conta, contas, preenchido, aoFechar, aoSalvar }: {
  conta: ContaPagar | null;
  /** Todas as contas da tela: é contra elas que a repetição é conferida. */
  contas: ContaPagar[];
  preenchido?: PreenchidoPeloBoleto | null;
  aoFechar: () => void; aoSalvar: () => void;
}) {
  const forn = useQuery({ queryKey: ['fornecedores'], queryFn: () => fornecedores() });
  const p = conta ? null : preenchido ?? null;
  // Editar conta que já existe nunca é palpite: o que está lá foi decidido.
  const palpites = p?.palpites ?? [];
  const [f, setF] = useState({
    descricao: conta?.descricao ?? p?.descricao ?? '',
    categoria: (conta?.categoria ?? p?.categoria ?? 'outros') as CategoriaConta,
    // valor_previsto pode ser NULL desde a 046 ("a definir") — o template
    // antigo escrevia a string literal "null" no campo, e o salvar de baixo
    // transformava isso em R$ 0,00. Dois bugs, uma linha.
    // Pelo boleto, o valor vai nos dois campos: o boleto É a conta que chegou.
    previsto: conta
      ? (conta.valor_previsto != null ? String(conta.valor_previsto).replace('.', ',') : '')
      : reais(p?.valor ?? null),
    real: conta
      ? (conta.valor_real != null ? String(conta.valor_real).replace('.', ',') : '')
      : reais(p?.valor ?? null),
    // Convênio lido do boleto vem sem data: o campo fica VAZIO de propósito —
    // "hoje" ali seria um vencimento inventado.
    vencimento: conta?.vencimento ?? (p ? p.vencimento ?? '' : hojeISO()),
    fornecedorId: conta?.fornecedor_id ?? p?.fornecedorId ?? '',
    observacao: conta?.observacao ?? '',
    codigo: conta?.codigo_barras ? formatarLinha(conta.codigo_barras) : p ? formatarLinha(p.codigo) : '',
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((a) => ({ ...a, [k]: v }));
  // O código de barras (077) é opcional, mas se veio tem que conferir: um
  // número trocado aqui chegaria ao banco no celular de quem paga.
  const codigo = f.codigo.trim() ? conferirCodigo(f.codigo) : null;
  const doCodigo = codigo?.ok ? dadosDoCodigo(codigo.linha, hojeISO()) : null;
  // A conta NOVA que já foi criada: se o código falhar depois, o "Salvar" de
  // novo atualiza esta, em vez de criar uma segunda conta igual.
  const idCriado = useRef<string | null>(null);
  const anexado = useRef(false);
  const [anexar, setAnexar] = useState(true);

  // A CONTA REPETIDA (18/09/2026, "cadastrei a mesma conta sem querer e não
  // alertou nem nada"): mesmo código trava; mesmo valor com vencimento a até
  // 3 dias, a conta fixa do mês ainda sem valor, ou a 2ª via do mesmo título
  // pedem confirmação. Vale para a conta digitada à mão também.
  const valorDoFormulario = paraNumero(f.real) ?? paraNumero(f.previsto);
  const linhaDoCodigo = codigo?.ok ? codigo.linha : null;
  const repetidas = contasParecidas({
    id: conta?.id ?? idCriado.current,
    valor: valorDoFormulario,
    vencimento: f.vencimento,
    codigo: linhaDoCodigo,
    categorias: [f.categoria],
  }, contas, mesmoTitulo);
  const suspeitas = [...repetidas.mesmoTitulo, ...repetidas.parecidas];
  const [eOutra, setEOutra] = useState(false);
  // Editando, o aviso só pede confirmação se o que identifica a conta mudou:
  // duas contas legítimas de mesmo valor e dia não podem travar toda edição.
  const mexeuNoQueIdentifica = !conta
    || valorDoFormulario !== valorDaConta(conta)
    || f.vencimento !== conta.vencimento
    || linhaDoCodigo !== (conta.codigo_barras ?? null);
  const pedeConfirmacao = suspeitas.length > 0 && mexeuNoQueIdentifica && !eOutra;
  const valorDifere = doCodigo !== null && doCodigo.valor !== null && valorDoFormulario !== null
    && Math.abs(doCodigo.valor - valorDoFormulario) >= 0.005;
  const diaDifere = doCodigo !== null && doCodigo.vencimento !== null && f.vencimento !== ''
    && doCodigo.vencimento !== f.vencimento;

  const salvar = useMutation({
    mutationFn: async () => {
      // O anexo é preparado ANTES de gravar: arquivo recusado (grande demais,
      // formato) para aqui, sem deixar conta nova para trás.
      const anexo = p?.arquivo && anexar && !anexado.current ? await prepararComprovante(p.arquivo) : null;
      // Conta e código numa transação só (078): boleto repetido desfaz a
      // conta nova junto — era o furo de gravar em duas chamadas.
      const id = await salvarContaECodigo({
        id: conta?.id ?? idCriado.current,
        descricao: f.descricao,
        categoria: f.categoria,
        // NULL é "a definir" (046) — zerar aqui apagava a natureza da conta.
        valorPrevisto: paraNumero(f.previsto),
        valorReal: paraNumero(f.real),
        vencimento: f.vencimento,
        fornecedorId: f.fornecedorId || null,
        observacao: f.observacao || null,
        codigo: linhaDoCodigo,
        // Só mexe no código se ele mudou aqui: outra tela pode ter gravado um
        // enquanto este formulário estava aberto com a cópia antiga.
        mexerCodigo: linhaDoCodigo !== (conta?.codigo_barras ?? null),
      });
      idCriado.current = id;
      // O boleto lido vai para a vaga do BOLETO (080) — o comprovante fica
      // livre para quando a conta for paga.
      if (anexo) {
        await salvarComprovanteConta({
          contaId: id, base64: anexo.base64, contentType: anexo.contentType, nome: anexo.nome,
          tipo: 'boleto',
        });
        anexado.current = true;
      }
    },
    onSuccess: aoSalvar,
  });

  return (
    <div className="cartao space-y-3 p-4">
      <h2 className="rotulo">{conta ? 'Editar conta' : 'Nova conta'}</h2>
      {p?.porque && palpites.length > 0 && (
        <p className="text-xs text-alerta">
          {p.porque} Os campos marcados como <strong>palpite</strong> foram
          preenchidos por isso — confira antes de salvar.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <RotuloDoCampo para="ct-desc" palpite={palpites.includes('descricao')}>Descrição</RotuloDoCampo>
          <input id="ct-desc" className="campo" value={f.descricao} autoFocus
                 onChange={(e) => set('descricao', e.target.value)} />
        </div>
        <div>
          <RotuloDoCampo para="ct-cat" palpite={palpites.includes('categoria')}>Categoria</RotuloDoCampo>
          <select id="ct-cat" className="campo" value={f.categoria}
                  onChange={(e) => set('categoria', e.target.value as CategoriaConta)}>
            {CATEGORIAS.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>
          {f.categoria === 'salarios' && (
            <p className="mt-1 text-xs text-alerta">{DICA_DA_CATEGORIA_SALARIOS}</p>
          )}
        </div>
        <div>
          <label className="rotulo" htmlFor="ct-prev">Valor previsto (R$)</label>
          <input id="ct-prev" className="campo" value={f.previsto} inputMode="decimal"
                 onChange={(e) => set('previsto', e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="ct-real">Valor da conta (opcional)</label>
          <input id="ct-real" className="campo" value={f.real} inputMode="decimal"
                 placeholder="quando a conta chegar"
                 onChange={(e) => set('real', e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="ct-venc">Vencimento</label>
          <input id="ct-venc" type="date" className="campo" value={f.vencimento}
                 onChange={(e) => set('vencimento', e.target.value)} />
        </div>
        <div>
          <RotuloDoCampo para="ct-forn" palpite={palpites.includes('fornecedor')}>Fornecedor (opcional)</RotuloDoCampo>
          <select id="ct-forn" className="campo" value={f.fornecedorId}
                  onChange={(e) => set('fornecedorId', e.target.value)}>
            <option value="">—</option>
            {(forn.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="rotulo" htmlFor="ct-obs">Observação</label>
        <input id="ct-obs" className="campo" value={f.observacao}
               onChange={(e) => set('observacao', e.target.value)} />
      </div>
      <div>
        <label className="rotulo" htmlFor="ct-codigo">Código de barras (opcional)</label>
        <input id="ct-codigo" className="campo font-mono text-sm" value={f.codigo}
               inputMode="numeric" autoComplete="off"
               placeholder="cole ou digite os números do boleto"
               onChange={(e) => set('codigo', e.target.value)} />
        {codigo && (
          <p className={`mt-1 text-xs ${codigo.ok ? 'text-ok'
            : soDigitos(f.codigo).length >= 44 ? 'text-erro' : 'text-mute'}`}>
            {codigo.ok
              ? `Confere — ${codigo.tipo === 'boleto' ? 'boleto de banco' : 'conta de luz, água ou imposto'}.`
              : codigo.erro}
          </p>
        )}
        {/* O código também confere o que foi digitado: ele carrega valor e,
            no boleto de banco, vencimento. Diferença aparece aqui. */}
        {doCodigo && (doCodigo.valor !== null || doCodigo.vencimento) && (
          <p className={`mt-1 text-xs ${valorDifere || diaDifere ? 'text-alerta' : 'text-mute'}`}>
            O código diz: {doCodigo.quem}
            {doCodigo.valor !== null ? ` · ${brl(doCodigo.valor)}` : ''}
            {doCodigo.vencimento ? ` · vence ${dataBR(doCodigo.vencimento)}` : ''}
            {valorDifere || diaDifere ? ' — diferente do que está no formulário. Confira o papel.' : ''}
          </p>
        )}
      </div>
      {p?.arquivo && (
        <label className="flex items-center gap-2 text-sm">
          <input id="ct-anexar" type="checkbox" checked={anexar}
                 onChange={(e) => setAnexar(e.target.checked)} />
          Guardar o boleto como anexo desta conta
        </label>
      )}
      {repetidas.mesmoCodigo && (
        <Aviso tipo="erro">
          Este boleto já está cadastrado: <strong>{repetidas.mesmoCodigo.descricao}</strong>,
          vence {dataBR(repetidas.mesmoCodigo.vencimento)}
          {repetidas.mesmoCodigo.pago_em ? `, pago em ${dataBR(repetidas.mesmoCodigo.pago_em)}` : ''}.
          Não lance de novo.
        </Aviso>
      )}
      {suspeitas.length > 0 && mexeuNoQueIdentifica && (
        <Aviso tipo="alerta">
          <p>Parece com conta que já está lançada:</p>
          <ul className="my-1 list-disc pl-5">
            {suspeitas.slice(0, 3).map((c) => (
              <li key={c.id}>
                {c.descricao} · vence {dataBR(c.vencimento)}
                {valorDaConta(c) !== null ? ` · ${brl(valorDaConta(c))}` : ' · valor a definir'}
                {c.pago_em ? ` · paga em ${dataBR(c.pago_em)}` : ''}
                {repetidas.mesmoTitulo.includes(c) ? ' — mesmo boleto, outra via' : ''}
              </li>
            ))}
          </ul>
          <label className="flex items-center gap-2">
            <input id="ct-outra" type="checkbox" checked={eOutra}
                   onChange={(e) => setEOutra(e.target.checked)} />
            É outra conta — salvar mesmo assim
          </label>
        </Aviso>
      )}
      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}
      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>Voltar</button>
        <button type="button" className="botao-primario flex-1 py-2 text-sm disabled:opacity-50"
                disabled={salvar.isPending || !f.descricao.trim() || !f.vencimento
                  || (codigo !== null && !codigo.ok) || repetidas.mesmoCodigo !== null || pedeConfirmacao}
                onClick={() => salvar.mutate()}>
          {salvar.isPending ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}

/** A REGRA da conta fixa: cadastra uma vez, o sistema gera 12 meses. */
export function FormularioRegra({ regra, aoFechar, aoSalvar }: {
  regra: ContaRecorrente | null; aoFechar: () => void; aoSalvar: () => void;
}) {
  const forn = useQuery({ queryKey: ['fornecedores'], queryFn: () => fornecedores() });
  const [f, setF] = useState({
    descricao: regra?.descricao ?? '',
    categoria: (regra?.categoria ?? 'aluguel') as CategoriaConta,
    valor: regra?.valor_previsto != null ? String(regra.valor_previsto).replace('.', ',') : '',
    dia: regra ? String(regra.dia_vencimento) : '10',
    inicio: regra?.data_inicio ?? hojeISO(),
    fim: regra?.data_fim ?? '',
    fornecedorId: regra?.fornecedor_id ?? '',
    observacao: regra?.observacao ?? '',
    ativa: regra?.ativa ?? true,
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((a) => ({ ...a, [k]: v }));

  const salvar = useMutation({
    mutationFn: () => salvarContaRecorrente({
      id: regra?.id ?? null,
      descricao: f.descricao,
      categoria: f.categoria,
      valor: paraNumero(f.valor),
      diaVencimento: Number(f.dia),
      dataInicio: f.inicio,
      dataFim: f.fim || null,
      fornecedorId: f.fornecedorId || null,
      observacao: f.observacao || null,
      ativa: f.ativa,
    }),
    onSuccess: aoSalvar,
  });

  return (
    <div className="cartao space-y-3 p-4">
      <h2 className="rotulo">{regra ? 'Editar conta fixa' : 'Nova conta fixa'}</h2>
      <p className="text-xs text-mute">
        Cadastre uma vez: o sistema cria os <strong>próximos 12 meses</strong> sozinho.
        Se o valor varia (luz, água), <strong>deixe o valor em branco</strong> —
        a conta vence na data certa como "a definir", e o número de verdade
        entra na hora de pagar.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="rotulo" htmlFor="rg-desc">Descrição</label>
          <input id="rg-desc" className="campo" value={f.descricao} autoFocus
                 onChange={(e) => set('descricao', e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="rg-cat">Categoria</label>
          <select id="rg-cat" className="campo" value={f.categoria}
                  onChange={(e) => set('categoria', e.target.value as CategoriaConta)}>
            {CATEGORIAS.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
          </select>
          {f.categoria === 'salarios' && (
            <p className="mt-1 text-xs text-alerta">{DICA_DA_CATEGORIA_SALARIOS}</p>
          )}
        </div>
        <div>
          <label className="rotulo" htmlFor="rg-valor">Valor estimado (R$ — vazio se varia)</label>
          <input id="rg-valor" className="campo" value={f.valor} inputMode="decimal"
                 placeholder="varia todo mês" onChange={(e) => set('valor', e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="rg-dia">Vence todo dia</label>
          <input id="rg-dia" className="campo" value={f.dia} inputMode="numeric"
                 onChange={(e) => set('dia', e.target.value)} />
          <p className="mt-1 text-xs text-dim">Dia 31 cai no último dia dos meses curtos.</p>
        </div>
        <div>
          <label className="rotulo" htmlFor="rg-ini">A partir de</label>
          <input id="rg-ini" type="date" className="campo" value={f.inicio}
                 onChange={(e) => set('inicio', e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="rg-fim">Até (opcional)</label>
          <input id="rg-fim" type="date" className="campo" value={f.fim}
                 onChange={(e) => set('fim', e.target.value)} />
        </div>
        <div>
          <label className="rotulo" htmlFor="rg-forn">Fornecedor (opcional)</label>
          <select id="rg-forn" className="campo" value={f.fornecedorId}
                  onChange={(e) => set('fornecedorId', e.target.value)}>
            <option value="">—</option>
            {(forn.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.ativa}
                   onChange={(e) => set('ativa', e.target.checked)} />
            Ativa
          </label>
        </div>
      </div>
      {regra && !f.ativa && (
        <Aviso tipo="alerta">
          Desativando, as ocorrências FUTURAS que ninguém tocou somem. O que já
          foi pago fica no histórico.
        </Aviso>
      )}
      {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}
      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>Voltar</button>
        <button type="button" className="botao-primario flex-1 py-2 text-sm disabled:opacity-50"
                disabled={salvar.isPending || !f.descricao.trim()}
                onClick={() => salvar.mutate()}>
          {salvar.isPending ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}
